import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import Cookies from "universal-cookie";

const cookies = new Cookies();

const isCommercial = () => {
  try {
    const hostname = window.location.hostname.toLowerCase();

    // Split the hostname into parts
    const parts = hostname.split(".");

    // Check if we have enough parts for a subdomain
    if (parts.length >= 2) {
      // First part should be 'c'
      return parts[0] === "c";
    }

    return false;
  } catch (error) {
    console.error("Error checking subdomain:", error);
    return false;
  }
};

const getAppType = () => {
  try {
    const hostname = window.location.hostname.toLowerCase();
    const parts = hostname.split(".");
    switch (parts[0]) {
      case "c":
        return "commercial";
      case "i":
        return "investment";
      default:
        return "residential";
    }
  } catch (error) {
    console.error("Error getting app type:", error);
    return "residential";
  }
};

interface ListingState {
  appType: string;
  isCommercial: boolean;
  listings: any;
  leadStatusOptions: any;
  selectedListing: any;
  selectedListingDetails: any;
  refetch: number;
  logged: boolean;
  filterValues: any;
  userType: any;
  selectedListingAttachmentData: any;
  selectedListingLeadStatusData: any;
  selectedListingLeadNoteData: any;
  userFavorites: any;
  favoritesShow: boolean;
  addListingShow: boolean;
  userReminders: any;
  remindersShow: boolean;
  userRoutes: any;
  routesShow: boolean;
  currentRoute: any;
  refetchStatus: boolean;
  refetchDetails: boolean;
  userData: any;
  teamActivityShow: boolean;
  teamFeatures: any;
  listView: boolean;
  triggerRegister: boolean;
  hoveredListing: any;
  mapCenter: any;
  mapZoom: number;
  printQueue: any;
}

const initialState: ListingState = {
  appType: getAppType(),
  isCommercial: isCommercial(),
  listings: [],
  leadStatusOptions: [],
  selectedListing: null,
  selectedListingDetails: null,
  refetch: Date.now(),
  logged: cookies.get("listings-app-login") ? true : false,
  filterValues: {
    //filterState: '',
    filterTime: "",
    //filterLeadOwner: '',
    filterLeadStatus: "",
    filterBounds: "",
    //filterName: '',
    //filterAddress: '',
  },
  userType: "",
  selectedListingAttachmentData: [],
  selectedListingLeadStatusData: [],
  selectedListingLeadNoteData: [],
  userFavorites: [],
  favoritesShow: false,
  addListingShow: false,
  userReminders: [],
  remindersShow: false,
  userRoutes: [],
  routesShow: false,
  currentRoute: null,
  refetchStatus: false,
  refetchDetails: false,
  userData: null,
  teamActivityShow: false,
  teamFeatures: [],
  listView: true,
  triggerRegister: false,
  hoveredListing: null,
  mapCenter: {
    lat: 37.09024,
    lng: -95.712891,
  },
  mapZoom: 13,
  printQueue: [],
};

export const listingSlice = createSlice({
  name: "listing",
  initialState,
  reducers: {
    setListings: (state, action: PayloadAction<any>) => {
      state.listings = action.payload;
    },
    setSelectedListing: (state, action: PayloadAction<any>) => {
      state.selectedListing = action.payload;
    },
    setSelectedListingDetails: (state, action: PayloadAction<any>) => {
      state.selectedListingDetails = action.payload;
    },
    setRefetch: (state, action: PayloadAction<boolean>) => {
      state.refetch = Date.now();
    },
    setLogged: (state, action: PayloadAction<boolean>) => {
      if (!action.payload) {
        localStorage.removeItem("listings-app-login");
        cookies.remove("listings-app-login");
        localStorage.removeItem("listings-app-api-token");
        cookies.remove("listings-app-api-token");
        localStorage.removeItem("listings-app-user-type");
        cookies.remove("listings-app-user-type");
      }
      state.logged = action.payload;
    },
    setFilterValues: (state, action: PayloadAction<any>) => {
      state.filterValues = action.payload;
    },
    setUserType: (state, action: PayloadAction<any>) => {
      state.userType = action.payload;
    },
    setSelectedListingAttachmentData: (state, action: PayloadAction<any>) => {
      state.selectedListingAttachmentData = action.payload;
    },
    pushToSelectedListingAttachmentData: (
      state,
      action: PayloadAction<any>
    ) => {
      state.selectedListingAttachmentData.push(action.payload);
    },
    setLeadStatusOptions: (state, action: PayloadAction<any>) => {
      state.leadStatusOptions = action.payload;
    },
    setSelectedListingLeadStatusData: (state, action: PayloadAction<any>) => {
      state.selectedListingLeadStatusData = action.payload;
    },
    pushToSelectedListingLeadStatusData: (
      state,
      action: PayloadAction<any>
    ) => {
      state.selectedListingLeadStatusData.push(action.payload);
    },
    setSelectedListingLeadNoteData: (state, action: PayloadAction<any>) => {
      state.selectedListingLeadNoteData = action.payload;
    },
    pushToSelectedListingLeadNoteData: (state, action: PayloadAction<any>) => {
      state.selectedListingLeadNoteData.push(action.payload);
    },
    setUserFavoritesData: (state, action: PayloadAction<any>) => {
      state.userFavorites = action.payload;
    },
    pushUserFavoritesData: (state, action: PayloadAction<any>) => {
      state.userFavorites.push(action.payload);
    },
    setFavoritesShow: (state, action: PayloadAction<boolean>) => {
      state.favoritesShow = action.payload;
    },
    setAddListingShow: (state, action: PayloadAction<boolean>) => {
      state.addListingShow = action.payload;
    },
    setUserRemindersData: (state, action: PayloadAction<any>) => {
      state.userReminders = action.payload;
    },
    pushUserRemindersData: (state, action: PayloadAction<any>) => {
      state.userReminders.push(action.payload);
    },
    setRemindersShow: (state, action: PayloadAction<boolean>) => {
      state.remindersShow = action.payload;
    },
    setUserRoutesData: (state, action: PayloadAction<any>) => {
      state.userRoutes = action.payload;
    },
    pushUserRoutesData: (state, action: PayloadAction<any>) => {
      state.userRoutes.push(action.payload);
    },
    setRoutesShow: (state, action: PayloadAction<boolean>) => {
      state.routesShow = action.payload;
    },
    setCurrentRoute: (state, action: PayloadAction<any>) => {
      state.currentRoute = action.payload;
    },
    setRefetchStatus: (state, action: PayloadAction<boolean>) => {
      state.refetchStatus = action.payload;
    },
    setRefetchDetails: (state, action: PayloadAction<boolean>) => {
      state.refetchDetails = action.payload;
    },
    setUserData: (state, action: PayloadAction<any>) => {
      state.userData = action.payload;
    },
    setTeamActivityShow: (state, action: PayloadAction<boolean>) => {
      state.teamActivityShow = action.payload;
    },
    setTeamFeatures: (state, action: PayloadAction<any>) => {
      state.teamFeatures = action.payload;
    },
    setListView: (state, action: PayloadAction<boolean>) => {
      state.listView = action.payload;
    },
    setTriggerRegister: (state, action: PayloadAction<boolean>) => {
      state.triggerRegister = action.payload;
    },
    setHoveredListing: (state, action: PayloadAction<any>) => {
      state.hoveredListing = action.payload;
    },
    setMapCenter: (state, action: PayloadAction<any>) => {
      state.mapCenter = action.payload;
    },
    setMapZoom: (state, action: PayloadAction<any>) => {
      state.mapZoom = action.payload;
    },
    setPrintQueue: (state, action: PayloadAction<any>) => {
      state.printQueue = action.payload;
    },
  },
});

export const {
  setListings,
  setSelectedListing,
  setSelectedListingDetails,
  setRefetch,
  setLogged,
  setFilterValues,
  setUserType,
  setSelectedListingAttachmentData,
  pushToSelectedListingAttachmentData,
  setLeadStatusOptions,
  setSelectedListingLeadStatusData,
  pushToSelectedListingLeadStatusData,
  setSelectedListingLeadNoteData,
  pushToSelectedListingLeadNoteData,
  setUserFavoritesData,
  pushUserFavoritesData,
  setFavoritesShow,
  setAddListingShow,
  pushUserRemindersData,
  setUserRemindersData,
  setRemindersShow,
  pushUserRoutesData,
  setRoutesShow,
  setUserRoutesData,
  setCurrentRoute,
  setRefetchStatus,
  setRefetchDetails,
  setUserData,
  setTeamActivityShow,
  setTeamFeatures,
  setListView,
  setTriggerRegister,
  setHoveredListing,
  setMapCenter,
  setMapZoom,
  setPrintQueue,
} = listingSlice.actions;

export default listingSlice.reducer;
